/**
 * CardIdBadge — exibe ID do card + botão de copiar pro clipboard (FEAT-V2-017).
 *
 * Renderizado no header de cards (DealDetailDialog, OsDetailDialog).
 * Click no ícone copia o ID técnico (UUID) + dispara toast inline temporário.
 *
 * Usa navigator.clipboard quando disponível, com fallback pra textarea
 * temporária (Safari iOS sem permissão).
 *
 * Fix BUG-V2-045: aceita `humanLabel` opcional pra exibir `#1000` em vez de
 * UUID raw. UUID continua sendo o que vai pro clipboard (usado em debug,
 * deeplinks, etc). Se humanLabel for null/undefined cai de volta no UUID
 * (compat retroativa + fallback defensivo quando numero_humano IS NULL).
 */

import { useState } from 'react';

interface CardIdBadgeProps {
  cardId: string;
  /**
   * Label humano (ex: "#1000"). Quando presente, é exibido em vez do UUID.
   * UUID continua sendo o conteúdo copiado pro clipboard.
   */
  humanLabel?: string | null;
  /** Variant: 'inline' (compacto, dentro de DialogTitle) ou 'block' (próprio elemento). */
  variant?: 'inline' | 'block';
}

async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
    // Fallback Safari/sem clipboard API
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

export function CardIdBadge({ cardId, humanLabel, variant = 'inline' }: CardIdBadgeProps) {
  const [copied, setCopied] = useState(false);
  // BUG-V2-045: exibe label humano se presente; UUID continua sendo o
  // valor copiado pro clipboard (debug/deeplink/contato com suporte).
  const display = humanLabel && humanLabel.length > 0 ? humanLabel : cardId;

  async function handleCopy() {
    const ok = await copyToClipboard(cardId);
    if (ok) {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    }
  }

  return (
    <span
      data-testid="card-id-badge"
      className={
        variant === 'inline'
          ? 'inline-flex items-center gap-1 text-xs text-ink-3 dark:text-ink-dark-3 font-mono'
          : 'flex items-center gap-1.5 text-xs text-ink-3 dark:text-ink-dark-3 font-mono mt-1'
      }
    >
      <span>ID:</span>
      <span data-testid="card-id-value">{display}</span>
      <button
        type="button"
        onClick={handleCopy}
        className="hover:text-red transition-colors px-1"
        aria-label="Copiar ID técnico do card"
        data-testid="card-id-copy-btn"
        title={`Copiar ID técnico (${cardId})`}
      >
        {copied ? (
          <span data-testid="card-id-copied" className="text-success">
            ✓ copiado
          </span>
        ) : (
          <span aria-hidden="true">📋</span>
        )}
      </button>
    </span>
  );
}
